import buildReportFilename from './reportFilename';

/**
 * Generate and download a PDF report.
 *
 * Generation runs inside a Web Worker so the main thread stays responsive
 * during the 30-65 second render.  The File System Access save dialog is
 * shown eagerly (before the worker starts) so the user gesture context is
 * preserved.
 *
 * @param {object}   opts
 * @param {object}   opts.config
 * @param {array}    opts.columns
 * @param {object}   opts.columnWidths
 * @param {array}    opts.data
 * @param {object}   opts.parameters
 * @param {object}   opts.paramHeaderWidths
 * @param {string}   opts.logoSrc
 * @param {string}   [opts.clientLogoSrc]   — base64 data URL for client logo (optional)
 * @param {string}   [opts.clientName]      — client name for confidentiality statement (optional)
 * @param {number}   [opts.rowsPerPage]
 * @param {number}   [opts.fontSizeOverride]
 * @param {function} [opts.isCancelled]      — () => bool
 * @param {function} [opts.onPhase]          — (phase: string, detail?: any) => void
 * @param {function} [opts.onProgress]       — (msg: string|null) => void  (toolbar indicator)
 * @param {string}   [opts.groupByLabel]    — label of the group-by parameter (for filename segment)
 * @param {function} [opts.onWorkerReady]    — (worker, rejectFn) => void  (caller stores ref for cancel)
 */
const exportReportPdf = async ({
  config,
  columns,
  columnWidths,
  data,
  parameters,
  paramHeaderWidths,
  logoSrc,
  clientLogoSrc,
  clientName,
  sortOrder,
  groupByField,
  groupByLabel,
  rowsPerPage,
  fontSizeOverride,
  qrDataUrl,
  dawnVersion,
  columnsByGroup,
  autoDownload = false,
  isCancelled,
  onPhase,
  onProgress,
  onWorkerReady,
}) => {
  try {
    if (onProgress) onProgress('Generating PDF...');

    // ── Phase 2: build document structure (instantaneous — just the prop set) ──
    if (onPhase) onPhase('building');
    const pageCount = rowsPerPage
      ? Math.ceil(Math.max(data.length, 1) / rowsPerPage)
      : 1;
    const filename = buildReportFilename(config.reportName, 'pdf', groupByLabel);

    // ── Eagerly open the save dialog while we still have the user gesture ──
    // Skip when autoDownload is true (separate-files grouped export — no dialog spam).
    let fileHandle = null;
    if (!autoDownload && window.showSaveFilePicker) {
      try {
        fileHandle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: 'PDF Document',
            accept: { 'application/pdf': ['.pdf'] },
          }],
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          // User cancelled the save dialog — bail out entirely
          if (onProgress) onProgress(null);
          return false;
        }
        // Unsupported / permission error — fall through to auto-download
      }
    }

    // ── Phase 3: render PDF in Web Worker (non-blocking) ──
    if (onPhase) onPhase('rendering', pageCount);

    const blob = await new Promise((resolve, reject) => {
      const worker = new Worker(
        new URL('../../../workers/pdfExportWorker.js', import.meta.url)
      );

      // Hand the worker reference + reject fn to the caller so it can
      // terminate the worker immediately on cancel.
      if (onWorkerReady) onWorkerReady(worker, reject);

      worker.onmessage = ({ data: result }) => {
        worker.terminate();
        if (result.type === 'done') {
          resolve(result.blob);
        } else {
          reject(new Error(result.message || 'PDF worker failed'));
        }
      };

      worker.onerror = (err) => {
        worker.terminate();
        reject(err);
      };

      worker.postMessage({
        config,
        columns,
        columnWidths,
        reportData: data,    // renamed — 'data' is reserved in MessageEvent
        parameters,
        paramHeaderWidths,
        logoSrc,
        clientLogoSrc,
        clientName,
        sortOrder,
        groupByField,
        rowsPerPage,
        fontSizeOverride,
        qrDataUrl,
        dawnVersion,
        columnsByGroup,
      });
    });

    // Check if cancelled while worker was running
    if (isCancelled && isCancelled()) {
      if (onPhase) onPhase('cancelled');
      if (onProgress) onProgress(null);
      return false;
    }

    // ── Phase 4: write file ──
    if (onPhase) onPhase('download');

    if (fileHandle) {
      // File System Access API — write to the handle the user already picked
      try {
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch (writeErr) {
        console.error('[reportPdfExporter] File write failed, falling back to download:', writeErr);
        // Fall through to blob-URL download
        fileHandle = null;
      }
    }

    if (!fileHandle) {
      // Fallback: trigger a browser download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    if (onPhase) onPhase('ready');
    if (onProgress) onProgress(null);
    return true;

  } catch (err) {
    // AbortError = worker was terminated by the cancel button
    if (err.name === 'AbortError' || (err.message && err.message.includes('cancel'))) {
      if (onPhase) onPhase('cancelled');
      if (onProgress) onProgress(null);
      return false;
    }
    if (onProgress) onProgress(null);
    throw err;
  }
};

export default exportReportPdf;
